import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import React from "react";
import Image from "next/image";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

// Utility to map menu item names to SVG filenames
const nameToSvg: Record<string, string> = {
  "5Whys": "5why.svg",
  "Action Plan": "action-plan.svg",
  "Action Plan Manager": "action-plan-manager.svg",
  "Action Plans Dashboard": "dashboard.svg",
  "Admin": "admin.svg",
  "Values": "values.svg",
  "Answers": "answers.svg",
  //'Audit Dashboard': 'audit-dashboard.svg',
  "Smart Goal Template": "smart-goal-template.svg",
  "Audit KPI Metric Target": "audit-kpi-metric-target.svg",
  "CEP KPI Metric Target": "audit-kpi-metric-target.svg",
  "My Audits": "my-audits.svg",
  "Company": "company.svg",
  "Company Facility": "company-facility.svg",
  "Company Setting": "company-settings.svg",
  "Attestation Configuration": "company-settings.svg",
  "Company Default Data": "company-settings.svg",
  "Company User": "company-user.svg",
  "Company Facility Group": "company-facility.svg",
  "Company Rules": "company-rules.svg",
  "Company Menus": "menu-management.svg",
  "Company Region Market": "company.svg",
  "Communication Log": "committee-meeting.svg",
  "Committee": "committee.svg",
  "Committee Meeting": "committee-meeting.svg",
  "Meeting Minutes History": "committee-meeting.svg",
  "DOC Vault": "policy-and-procedure-management.svg",
  "Dashboard": "dashboard.svg",
  "PBJ Report": "dashboard.svg",
  "Feature Dashboard": "dashboard.svg",
  "QAPI Plan Dashboard": "dashboard.svg",
  "QAPI Data Manager": "facility-assessment.svg",
  "QAPI Data Management": "facility-assessment.svg",
  "My QAPI Data": "facility-assessment.svg",
  "Facility Assessment": "facility-assessment.svg",
  "Facility Evaluation": "facility-assessment.svg",
  "Emergency Preparedness": "facility-assessment.svg",
  "AP Report": "dashboard.svg",
  "AP Report SP List": "dashboard.svg",
  "Company SP List": "dashboard.svg",
  "AP Report Mapping": "dashboard.svg",
  "Grievance": "grievance.svg",
  "Grievance Dashboard": "grievance-dashboard.svg",
  "Patient Consent": "patient-consent.svg",
  "Patient Consent Dashboard": "dashboard.svg",
  "External Patient Consent Dashboard": "dashboard.svg",
  "Survey Management Dashboard": "dashboard.svg",
  "Impact Feedback": "dashboard.svg",
  "Grievance Dashboard V2": "grievance-dashboard.svg",
  "External Grievance Dashboard": "dashboard.svg",
  "Incident Dashboard": "incident-dashboard.svg",
  "Resident Incident Dashboard": "incident-dashboard.svg",
  "Incident Investigation Dashboard": "incident-dashboard.svg",
  "Incident Notification Rule": "incident-notification.svg",
  "Incident RCA": "incident-rca.svg",
  "Incident RCA 5Whys Template": "5why.svg",
  "Incident Report": "incident-report.svg",
  "Incidents": "incidents.svg",
  "Facility Resident Risk Event Document": "incident-report.svg",
  "PIP Plan": "pip-plan.svg",
  "Performance Improvement Plan": "pip-plan.svg",
  "Policy & Procedure Management": "policy-and-procedure-management.svg",
  "My Policy": "policy-and-procedure-management.svg",
  "Provider": "provider.svg",
  "User Menus Access": "user-menus-access.svg",
  "Menu Management": "menu-management.svg",
  "Form Manager": "form-manager.svg",
  "Form Management": "form-management.svg",
  "App Feature Form": "form-management.svg",
  "Feature Form Mapping": "form-management.svg",
  "App Feature Dashboard Metric": "dashboard.svg",
  "App Feature List SP": "form-management.svg",
  "App Notifications": "incident-notification.svg",
  "Feature Communication": "incident-notification.svg",
  "App Metric Target": "incident-notification.svg",
  "Audit Manager": "audit-manager.svg",
  "Audit Management": "audit-management.svg",
  "Training": "training.svg",
  "Training Management": "training-management.svg",
  "Academy Management": "training-management.svg",
  "Academy Release": "training-management.svg",
  "My Academy": "training-management.svg",
  "Company Metric": "dashboard.svg",
  "Company Metric Data": "dashboard.svg",
  "Company Metric Threshold": "dashboard.svg",
  "MTAP Metric": "action-plan-manager.svg",
  "MTAP Facility Management": "action-plan-manager.svg",
  "My MTAP": "action-plan.svg",
  "MTAP Dashboard": "dashboard.svg",
  "Employee Questionnaire": "form-management.svg",
  "OSHA Dashboard": "dashboard.svg",
  "OSHA Management": "dashboard.svg",
  "OSHA Approval": "dashboard.svg",
  "OSHA Questionnaire Action": "form-management.svg",
  "OSHA Questionnaire Transition": "form-management.svg",
  "Vax Questionnaire Action": "vax-questionnaire-action.svg",
  "Vax Questionnaire Transition": "vax-questionnaire-action.svg",
  "CEP Manager": "audit-manager.svg",
  "CEP Management": "audit-management.svg",
  "My Critical Element Pathways": "my-audits.svg",
  "FAQ Management": "admin.svg",
  "Feature Document": "admin.svg",
  "FAQ": "admin.svg",
  "Download Tracking": "admin.svg",
  "Admin Dashboard": "admin.svg",
  "CMS Quality Measures": "facility-assessment.svg",
  "Global Legal Setting": "admin.svg",
  "Occupational Health": "admin.svg",
};

// Render SVG icon if available, else fallback
function getMenuIcon(name: string, fallback: React.ReactNode, color?: string) {
  const svgFile = nameToSvg[name];
  if (svgFile) {
    // Use next/image for public/icons
    return (
      <Image
        src={`/icons/${svgFile}`}
        alt={name}
        width={24}
        height={24}
        style={{
          display: "block",
          filter: color === "white" ? "invert(1) brightness(2)" : undefined,
        }}
      />
    );
  }
  // If fallback is a MUI icon, try to clone and set color
  if (color && React.isValidElement(fallback)) {
    const fallbackElement = fallback as React.ReactElement<any>;
    return React.cloneElement(fallbackElement, {
      style: { color, ...(fallbackElement.props.style || {}) },
    });
  }
  return fallback;
}

const menuItems = [
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: (color?: string) =>
      getMenuIcon("Dashboard", <DashboardIcon />, color),
  },
  {
    name: "Feature Dashboard",
    href: "/dashboard/feature-dashboard",
    icon: (color?: string) =>
      getMenuIcon("Feature Dashboard", <DashboardIcon />, color),
  },
  {
    name: "Facility Assessment",
    href: "/dashboard/facility-assessment",
    icon: (color?: string) =>
      getMenuIcon("Facility Assessment", <DashboardIcon />, color),
  },
  {
    name: "Facility Assessment Dashboard",
    href: "/dashboard/facility-assessment-dashboard",
    icon: (color?: string) =>
      getMenuIcon("Facility Assessment", <DashboardIcon />, color),
  },
  {
    name: "My Facility Assessment",
    href: "/dashboard/my-facility-assessment",
    icon: (color?: string) =>
      getMenuIcon("My Facility Assessment", <DashboardIcon />, color),
  },
  {
    name: "Survey Insights",
    href: "/dashboard/survey-insights",
    icon: (color?: string) =>
      getMenuIcon("Facility Assessment", <DashboardIcon />, color),
  },
  {
    name: "Facility Evaluation",
    href: "/dashboard/facility-evaluation",
    pageTitle: "Facility Evaluation",
    icon: (color?: string) =>
      getMenuIcon("Facility Evaluation", <DashboardIcon />, color),
  },
  {
    name: "Reports",
    href: "/dashboard/reports",
    pageTitle: "Reports",
    icon: (color?: string) => getMenuIcon("Reports", <TrendingUpIcon />, color),
  },
  {
    name: "Company SP List",
    href: "/dashboard/company-sp-list",
    pageTitle: "Company SP List",
    icon: (color?: string) =>
      getMenuIcon("Company SP List", <TrendingUpIcon />, color),
  },
  {
    name: "AP Report Mapping",
    href: "/dashboard/ap-report-mapping",
    pageTitle: "AP Report Mapping",
    icon: (color?: string) =>
      getMenuIcon("AP Report Mapping", <TrendingUpIcon />, color),
  },
  {
    name: "Smart Goal Template",
    href: "/dashboard/smart-goal-template",
    icon: (color?: string) =>
      getMenuIcon("Smart Goal Template", <PersonIcon />, color),
  },
  {
    name: "PIP Plan",
    href: "/dashboard/pip-plan",
    icon: (color?: string) => getMenuIcon("PIP Plan", <PersonIcon />, color),
  },
  {
    name: "Performance Improvement Plan",
    href: "/dashboard/pip",
    icon: (color?: string) =>
      getMenuIcon("Performance Improvement Plan", <TrendingUpIcon />, color),
  },
  {
    name: "Edit PIP",
    href: "/dashboard/pip/edit",
    pageTitle: "Edit PIP",
    icon: (color?: string) =>
      getMenuIcon("Performance Improvement Plan", <PersonIcon />, color),
  },
  {
    name: "Action Plan Manager",
    href: "/dashboard/action-plan",
    icon: (color?: string) =>
      getMenuIcon("Action Plan Manager", <PersonIcon />, color),
  },
  {
    name: "My Action Plans",
    href: "/dashboard/my-action-plan",
    icon: (color?: string) => getMenuIcon("Action Plan", <PersonIcon />, color),
  },
  {
    name: "Policy & Procedure Management",
    href: "/dashboard/policy-procedure",
    icon: (color?: string) =>
      getMenuIcon("Policy & Procedure Management", <PersonIcon />, color),
  },
  {
    name: "My Policy & Procedure",
    href: "/dashboard/my-policy",
    icon: (color?: string) => getMenuIcon("My Policy", <PersonIcon />, color),
  },
  {
    name: "Incidents",
    href: "/dashboard/incidents",
    icon: (color?: string) => getMenuIcon("Incidents", <PersonIcon />, color),
  },
  {
    name: "Incident Entry",
    href: "/dashboard/incident-entry",
    icon: (color?: string) =>
      getMenuIcon("Incident Report", <PersonIcon />, color),
  },
  {
    name: "Incident Report",
    href: "/dashboard/incident-report",
    icon: (color?: string) =>
      getMenuIcon("Incident Report", <PersonIcon />, color),
  },
  {
    name: "Incident Notification Rule",
    href: "/dashboard/incident-notification-rules",
    icon: (color?: string) =>
      getMenuIcon("Incident Notification Rule", <PersonIcon />, color),
  },
  {
    name: "Incident Dashboard",
    href: "/dashboard/incident-dashboard",
    icon: (color?: string) =>
      getMenuIcon("Incident Dashboard", <DashboardIcon />, color),
  },
  {
    name: "Facility Resident Risk Event Document",
    href: "/dashboard/facility-resident-risk-event-document",
    pageTitle: "Facility Resident Risk Event Document",
    icon: (color?: string) =>
      getMenuIcon(
        "Facility Resident Risk Event Document",
        <PersonIcon />,
        color,
      ),
  },
  {
    name: "Resident Incident Dashboard",
    href: "/dashboard/resident-incident-dashboard",
    pageTitle: "Resident Incident Dashboard",
    icon: (color?: string) =>
      getMenuIcon("Resident Incident Dashboard", <DashboardIcon />, color),
  },
  {
    name: "Incident Investigation Dashboard",
    href: "/dashboard/incident-investigation-dashboard",
    icon: (color?: string) =>
      getMenuIcon("Incident Investigation Dashboard", <DashboardIcon />, color),
  },
  {
    name: "Incident RCA 5Whys Template",
    href: "/dashboard/incident-rca-5whys-template",
    icon: (color?: string) =>
      getMenuIcon("Incident RCA 5Whys Template", <PersonIcon />, color),
  },
  {
    name: "Incident RCA",
    href: "/dashboard/incident-rca",
    icon: (color?: string) =>
      getMenuIcon("Incident RCA", <PersonIcon />, color),
  },
  {
    name: "Incident Investigation",
    href: "/dashboard/incident-investigation",
    icon: (color?: string) =>
      getMenuIcon("Incident RCA", <PersonIcon />, color),
  },
  {
    name: "Grievance",
    href: "/dashboard/grievance",
    icon: (color?: string) => getMenuIcon("Grievance", <PersonIcon />, color),
  },
  {
    name: "Grievance Management",
    href: "/dashboard/grievance-management",
    icon: (color?: string) => getMenuIcon("Grievance", <PersonIcon />, color),
  },
  {
    name: "Grievance Dashboard",
    href: "/dashboard/grievance-dashboard",
    icon: (color?: string) =>
      getMenuIcon("Grievance Dashboard", <DashboardIcon />, color),
  },
  {
    name: "External Grievance Dashboard",
    href: "/dashboard/grievance/external",
    icon: (color?: string) =>
      getMenuIcon("External Grievance Dashboard", <DashboardIcon />, color),
  },
  {
    name: "Grievance Transition",
    href: "/dashboard/grievance-transition",
    icon: (color?: string) => getMenuIcon("Grievance", <PersonIcon />, color),
  },
  {
    name: "Action Plans Dashboard",
    href: "/dashboard/action-plans-dashboard",
    icon: (color?: string) =>
      getMenuIcon("Action Plans Dashboard", <DashboardIcon />, color),
  },
  {
    name: "Committee",
    href: "/dashboard/committee",
    icon: (color?: string) =>
      getMenuIcon("Committee", <DashboardIcon />, color),
  },

  {
    name: "Committee Meeting",
    href: "/dashboard/committee-meeting",
    icon: (color?: string) =>
      getMenuIcon("Committee Meeting", <DashboardIcon />, color),
  },
  {
    name: "Meeting Minutes History",
    href: "/dashboard/meeting-minutes-history",
    icon: (color?: string) =>
      getMenuIcon("Meeting Minutes History", <DashboardIcon />, color),
  },
  {
    name: "DOC Vault",
    href: "/dashboard/doc-vault",
    icon: (color?: string) => getMenuIcon("DOC Vault", <PersonIcon />, color),
  },

  {
    name: "Occupational Health",
    href: "/dashboard/occupational-health",
    icon: (color?: string) => getMenuIcon("Occupational Health", <PersonIcon />, color),
  },

  {
    name: "My Audits",
    href: "/dashboard/my-audit",
    icon: (color?: string) => getMenuIcon("My Audits", <PersonIcon />, color),
  },

  {
    name: "Environmental Management",
    href: "/dashboard/environmental-management",
    icon: (color?: string) => getMenuIcon("My Audits", <PersonIcon />, color),
  },

  {
    name: "Employee Health Form Manager",
    href: "/dashboard/employee-health-form-manager",
    icon: (color?: string) => getMenuIcon("Provider", <PersonIcon />, color),
  },
  {
    name: "Employee Health",
    href: "/dashboard/employee-health",
    icon: (color?: string) => getMenuIcon("Provider", <PersonIcon />, color),
  },
  {
    name: "Employee Health Review",
    href: "/dashboard/employee-health-review",
    icon: (color?: string) => getMenuIcon("Provider", <PersonIcon />, color),
  },
  {
    name: "OSHA Dashboard",
    href: "/dashboard/osha-dashboard",
    pageTitle: "OSHA Dashboard",
    icon: (color?: string) =>
      getMenuIcon("OSHA Dashboard", <DashboardIcon />, color),
  },
  {
    name: "OSHA Management",
    href: "/dashboard/osha-management",
    pageTitle: "OSHA Management",
    icon: (color?: string) =>
      getMenuIcon("OSHA Management", <PersonIcon />, color),
  },
  {
    name: "OSHA Approval",
    href: "/dashboard/osha-approval",
    pageTitle: "OSHA Approval",
    icon: (color?: string) =>
      getMenuIcon("OSHA Approval", <PersonIcon />, color),
  },
  {
    name: "OSHA Questionnaire Action",
    href: "/dashboard/osha-questionnaire-action",
    icon: (color?: string) =>
      getMenuIcon("OSHA Questionnaire Action", <PersonIcon />, color),
  },
  {
    name: "Vax Questionnaire Action",
    href: "/dashboard/vax-questionnaire-action",
    icon: (color?: string) =>
      getMenuIcon("Vax Questionnaire Action", <PersonIcon />, color),
  },
  {
    name: "OSHA Questionnaire Transition",
    href: "/dashboard/osha-questionnaire-transition",
    icon: (color?: string) =>
      getMenuIcon("OSHA Questionnaire Transition", <PersonIcon />, color),
  },
  {
    name: "Season Management",
    href: "/dashboard/season-management",
    icon: (color?: string) =>
      getMenuIcon("Season Management", <PersonIcon />, color),
  },
  {
    name: "Vax Questionnaire Transition",
    href: "/dashboard/vax-questionnaire-transition",
    icon: (color?: string) =>
      getMenuIcon("Vax Questionnaire Transition", <PersonIcon />, color),
  },
  {
    name: "Vax Management",
    href: "/dashboard/vax-management",
    icon: (color?: string) => getMenuIcon("Provider", <PersonIcon />, color),
  },
  {
    name: "Vaccination Approval",
    href: "/dashboard/vaccination-approval",
    icon: (color?: string) =>
      getMenuIcon("Vaccination Approval", <PersonIcon />, color),
  },
  {
    name: "Vax Dashboard",
    href: "/dashboard/vax-dashboard",
    icon: (color?: string) =>
      getMenuIcon("Vax Dashboard", <DashboardIcon />, color),
  },

  {
    name: "Task Manager",
    href: "/dashboard/task-manager",
    icon: (color?: string) => getMenuIcon("Training", <PersonIcon />, color),
  },
  {
    name: "Task Report",
    href: "/dashboard/task-report",
    icon: (color?: string) => getMenuIcon("Training", <PersonIcon />, color),
  },
  {
    name: "Task Approval",
    href: "/dashboard/task-approval",
    icon: (color?: string) => getMenuIcon("Training", <PersonIcon />, color),
  },

  {
    name: "Task Manager V2",
    href: "/dashboard/task-manager-v2",
    icon: (color?: string) => getMenuIcon("Training", <PersonIcon />, color),
  },
  {
    name: "Task Dashboard",
    href: "/dashboard/task-dashboard",
    icon: (color?: string) => getMenuIcon("Training", <PersonIcon />, color),
  },
  {
    name: "PBJ Report",
    href: "/dashboard/pbj-report",
    pageTitle: "PBJ Report",
    icon: (color?: string) =>
      getMenuIcon("PBJ Report", <DashboardIcon />, color),
  },

  {
    name: "Task Reporter Configuration",
    href: "/dashboard/task-reporter-configuration",
    icon: (color?: string) =>
      getMenuIcon("Company Rules", <PersonIcon />, color),
  },

  {
    name: "Company",
    href: "/dashboard/company",
    icon: (color?: string) => getMenuIcon("Company", <PersonIcon />, color),
  },
  {
    name: "Company Facility",
    href: "/dashboard/company-facility",
    icon: (color?: string) =>
      getMenuIcon("Company Facility", <PersonIcon />, color),
  },
  {
    name: "Company User",
    href: "/dashboard/company-user",
    icon: (color?: string) =>
      getMenuIcon("Company User", <PersonIcon />, color),
  },
  {
    name: "Company User Facility",
    href: "/dashboard/company-user-facility",
    icon: (color?: string) =>
      getMenuIcon("Company User", <PersonIcon />, color),
  },
  {
    name: "Company Facility Group",
    href: "/dashboard/company-facility-group",
    icon: (color?: string) =>
      getMenuIcon("Company Facility Group", <PersonIcon />, color),
  },
  {
    name: "Company Setting",
    href: "/dashboard/company-settings",
    icon: (color?: string) =>
      getMenuIcon("Company Setting", <PersonIcon />, color),
  },
  {
    name: "Attestation Configuration",
    href: "/dashboard/attestation-configuration",
    icon: (color?: string) =>
      getMenuIcon("Attestation Configuration", <PersonIcon />, color),
  },
  {
    name: "Answers",
    href: "/dashboard/answers",
    icon: (color?: string) => getMenuIcon("Answers", <PersonIcon />, color),
  },
  {
    name: "User Menus Access",
    href: "/dashboard/user-menus",
    icon: (color?: string) =>
      getMenuIcon("User Menus Access", <PersonIcon />, color),
  },
  {
    name: "Company Rules",
    href: "/dashboard/company-rules",
    icon: (color?: string) =>
      getMenuIcon("Company Rules", <PersonIcon />, color),
  },
  {
    name: "Company Menus",
    href: "/dashboard/company-menus",
    icon: (color?: string) =>
      getMenuIcon("Company Menus", <PersonIcon />, color),
  },
  {
    name: "Company Region Market",
    href: "/dashboard/company-region-market",
    icon: (color?: string) =>
      getMenuIcon("Company Region Market", <PersonIcon />, color),
  },
  {
    name: "Company Task List",
    href: "/dashboard/company-task-list",
    icon: (color?: string) =>
      getMenuIcon("Company Rules", <PersonIcon />, color),
  },
  {
    name: "Company Task Reporters",
    href: "/dashboard/task-reporters",
    icon: (color?: string) =>
      getMenuIcon("Company Rules", <PersonIcon />, color),
  },
  {
    name: "User Role Settings",
    href: "/dashboard/user-role-settings",
    icon: (color?: string) =>
      getMenuIcon("Company Rules", <PersonIcon />, color),
  },
  {
    name: "Feature Role Settings",
    href: "/dashboard/feature-role-settings",
    icon: (color?: string) =>
      getMenuIcon("Company Rules", <SettingsIcon />, color),
  },
  {
    name: "Feature Role Access Settings",
    href: "/dashboard/feature-role-access-setting",
    icon: (color?: string) =>
      getMenuIcon("Company Rules", <SettingsIcon />, color),
  },
  {
    name: "Communication Log",
    href: "/dashboard/communication-log",
    icon: (color?: string) =>
      getMenuIcon("Communication Log", <PersonIcon />, color),
  },
  {
    name: "Values",
    href: "/dashboard/values",
    icon: (color?: string) => getMenuIcon("Values", <PersonIcon />, color),
  },
  {
    name: "Menu Management",
    href: "/dashboard/menu-management",
    icon: (color?: string) =>
      getMenuIcon("Menu Management", <PersonIcon />, color),
  },
  {
    name: "Admin Dashboard",
    href: "/dashboard/admin-dashboard",
    icon: (color?: string) =>
      getMenuIcon("Admin Dashboard", <DashboardIcon />, color),
  },
  {
    name: "Download Tracking",
    href: "/dashboard/download-tracking",
    icon: (color?: string) =>
      getMenuIcon("Download Tracking", <PersonIcon />, color),
  },
  {
    name: "FAQ Management",
    href: "/dashboard/faq-management",
    icon: (color?: string) =>
      getMenuIcon("FAQ Management", <PersonIcon />, color),
  },
  {
    name: "Feature Document",
    href: "/dashboard/feature-document",
    icon: (color?: string) =>
      getMenuIcon("Feature Document", <PersonIcon />, color),
  },
  {
    name: "FAQ",
    href: "/dashboard/faq",
    icon: (color?: string) => getMenuIcon("FAQ", <PersonIcon />, color),
  },
  {
    name: "Global Legal Setting",
    href: "/dashboard/global-legal-setting",
    icon: (color?: string) =>
      getMenuIcon("Global Legal Setting", <PersonIcon />, color),
  },
  {
    name: "Company Default Data",
    href: "/dashboard/company-default-data",
    icon: (color?: string) =>
      getMenuIcon("Company Default Data", <PersonIcon />, color),
  },
  {
    name: "App Feature Form",
    href: "/dashboard/app-feature-form",
    icon: (color?: string) =>
      getMenuIcon("App Feature Form", <PersonIcon />, color),
  },
  {
    name: "Feature Form Mapping",
    href: "/dashboard/feature-form-mapping",
    icon: (color?: string) =>
      getMenuIcon("Feature Form Mapping", <PersonIcon />, color),
  },
  {
    name: "App Feature Form Assignment Cadence",
    href: "/dashboard/app-feature-form-assignment-cadence",
    icon: (color?: string) =>
      getMenuIcon("App Feature Form Assignment Cadence", <PersonIcon />, color),
  },
  {
    name: "App Feature Dashboard Metric",
    href: "/dashboard/app-feature-dashboard-metric",
    icon: (color?: string) =>
      getMenuIcon("App Feature Dashboard Metric", <DashboardIcon />, color),
  },
  {
    name: "App Feature SP List",
    href: "/dashboard/app-feature-sp-list",
    icon: (color?: string) =>
      getMenuIcon("App Feature List SP", <PersonIcon />, color),
  },
  {
    name: "App Notifications",
    href: "/dashboard/app-notifications",
    icon: (color?: string) =>
      getMenuIcon("App Notifications", <PersonIcon />, color),
  },
  {
    name: "Feature Communication",
    href: "/dashboard/feature-communication",
    icon: (color?: string) =>
      getMenuIcon("Feature Communication", <PersonIcon />, color),
  },
  {
    name: "App Metric Target",
    href: "/dashboard/app-metric-target",
    icon: (color?: string) =>
      getMenuIcon("App Metric Target", <PersonIcon />, color),
  },
  {
    name: "Cron Job Manager",
    href: "/dashboard/cron-job-manager",
    icon: (color?: string) =>
      getMenuIcon("Cron Job Manager", <PersonIcon />, color),
  },
  {
    name: "Form Manager",
    href: "/dashboard/form-manager",
    icon: (color?: string) =>
      getMenuIcon("Form Management", <PersonIcon />, color),
  },
  {
    name: "Form Wizard",
    href: "/dashboard/form-wizard?new=true",
    activePath: "/dashboard/form-wizard",
    icon: (color?: string) =>
      getMenuIcon("Form Management", <PersonIcon />, color),
  },
  {
    name: "Form Actions & Transitions",
    href: "/dashboard/form-actions-transitions",
    icon: (color?: string) =>
      getMenuIcon("Form Management", <PersonIcon />, color),
  },
  {
    name: "Form Settings",
    href: "/dashboard/form-settings",
    activePath: "/dashboard/form-settings",
    icon: (color?: string) =>
      getMenuIcon("Form Management", <PersonIcon />, color),
  },
  {
    name: "Audit Management",
    href: "/dashboard/audit-management-v2",
    icon: (color?: string) =>
      getMenuIcon("Audit Management", <PersonIcon />, color),
  },
  {
    name: "Audit KPI Metric Target",
    href: "/dashboard/audit-kpi-metric-target",
    icon: (color?: string) =>
      getMenuIcon("Audit KPI Metric Target", <PersonIcon />, color),
  },
  {
    name: "Audit Dashboard",
    href: "/dashboard/audit-dashboard",
    icon: (color?: string) =>
      getMenuIcon("Audit Dashboard", <DashboardIcon />, color),
  },
  {
    name: "CEP Management",
    href: "/dashboard/cep-management",
    icon: (color?: string) =>
      getMenuIcon("CEP Management", <PersonIcon />, color),
  },
  {
    name: "Emergency Preparedness",
    href: "/dashboard/emergency-preparedness",
    icon: (color?: string) =>
      getMenuIcon("Emergency Preparedness", <DashboardIcon />, color),
  },
  {
    name: "CEP KPI Metric Target",
    href: "/dashboard/cep-kpi-metric-target",
    icon: (color?: string) =>
      getMenuIcon("CEP KPI Metric Target", <PersonIcon />, color),
  },
  {
    name: "My Critical Element Pathways",
    href: "/dashboard/my-cep",
    icon: (color?: string) =>
      getMenuIcon("My Critical Element Pathways", <PersonIcon />, color),
  },
  {
    name: "CEP Focus Area Link",
    href: "/dashboard/cep-focus-area-link",
    icon: (color?: string) =>
      getMenuIcon("My Critical Element Pathways", <PersonIcon />, color),
  },

  {
    name: "Care Compass Management",
    href: "/dashboard/care-compass-management",
    icon: (color?: string) =>
      getMenuIcon("Care Compass Management", <PersonIcon />, color),
  },
  {
    name: "Care Compass KPI Metric Target",
    href: "/dashboard/care-compass-kpi-metric-target",
    icon: (color?: string) =>
      getMenuIcon("Care Compass KPI Metric Target", <PersonIcon />, color),
  },
  {
    name: "My Care Compass",
    href: "/dashboard/my-care-compass",
    icon: (color?: string) =>
      getMenuIcon("My Care Compass", <PersonIcon />, color),
  },

  {
    name: "QAPI Data Management",
    href: "/dashboard/qapi-data-management",
    icon: (color?: string) =>
      getMenuIcon("QAPI Data Management", <PersonIcon />, color),
  },
  {
    name: "QAPI Data KPI Metric Target",
    href: "/dashboard/qapi-data-kpi-metric-target",
    icon: (color?: string) =>
      getMenuIcon("QAPI Data KPI Metric Target", <PersonIcon />, color),
  },
  {
    name: "My QAPI Data",
    href: "/dashboard/my-qapi-data",
    icon: (color?: string) =>
      getMenuIcon("My QAPI Data", <PersonIcon />, color),
  },
  {
    name: "QAPI Data Dashboard",
    href: "/dashboard/qapi-data-dashboard",
    icon: (color?: string) =>
      getMenuIcon("QAPI Data Dashboard", <DashboardIcon />, color),
  },

  {
    name: "Environmental Services Management",
    href: "/dashboard/environmental-services/environmental-services-management",
    icon: (color?: string) =>
      getMenuIcon("QAPI Data Management", <PersonIcon />, color),
  },
  {
    name: "My Environmental Services",
    href: "/dashboard/environmental-services/my-environmental-services",
    icon: (color?: string) =>
      getMenuIcon("My QAPI Data", <PersonIcon />, color),
  },
  {
    name: "Environmental Services Dashboard",
    href: "/dashboard/environmental-services/environmental-services-dashboard",
    icon: (color?: string) =>
      getMenuIcon("QAPI Data Dashboard", <DashboardIcon />, color),
  },

  /*{
    name: 'QAPI Plan Manager',
    href: '',
    icon: (color?: string) => getMenuIcon('QAPI Plan Manager', <PersonIcon />, color),
    subItems: [
      { name: 'QAPI Plan Management', href: '/dashboard/qapi-plan-management', icon: (color?: string) => getMenuIcon('QAPI Plan Management', <PersonIcon />, color) },
    ],
  },
  { name: 'My QAPI Plan', href: '/dashboard/my-qapi-plan', icon: (color?: string) => getMenuIcon('My QAPI Plan', <PersonIcon />, color) },
  { name: 'QAPI Plan Dashboard', href: '/dashboard/qapi-plan-dashboard', icon: (color?: string) => getMenuIcon('QAPI Plan Dashboard', <DashboardIcon />, color) },*/
  {
    name: "Training Management",
    href: "/dashboard/training-management",
    icon: (color?: string) =>
      getMenuIcon("Training Management", <PersonIcon />, color),
  },
  {
    name: "Academy Management",
    href: "/dashboard/academy-management",
    icon: (color?: string) =>
      getMenuIcon("Academy Management", <PersonIcon />, color),
  },
  {
    name: "Academy Release",
    href: "/dashboard/academy-release",
    icon: (color?: string) =>
      getMenuIcon("Academy Release", <PersonIcon />, color),
  },
  {
    name: "My Academy",
    href: "/dashboard/my-academy",
    icon: (color?: string) => getMenuIcon("My Academy", <PersonIcon />, color),
  },

  {
    name: "Survey Alert",
    href: "/dashboard/survey-alert",
    icon: (color?: string) =>
      getMenuIcon("Survey Alert", <PersonIcon />, color),
  },
  {
    name: "Impact Feedback",
    href: "/dashboard/impact-feedback",
    icon: (color?: string) =>
      getMenuIcon("Impact Feedback", <TrendingUpIcon />, color),
  },
  {
    name: "Survey Management",
    href: "/dashboard/survey-management",
    icon: (color?: string) =>
      getMenuIcon("Survey Management", <PersonIcon />, color),
  },
  {
    name: "Survey Management Dashboard",
    href: "/dashboard/survey-management-dashboard",
    icon: (color?: string) =>
      getMenuIcon("Survey Management Dashboard", <DashboardIcon />, color),
  },
  {
    name: "Survey Type Link",
    href: "/dashboard/survey-type-link",
    icon: (color?: string) =>
      getMenuIcon("Survey Type Link", <PersonIcon />, color),
  },
  {
    name: "CMS Quality Measures",
    href: "/dashboard/cms-quality-measures",
    activePath: "/dashboard/cms-quality-measures",
    icon: (color?: string) =>
      getMenuIcon("CMS Quality Measures", <TrendingUpIcon />, color),
  },
  {
    name: "Patient Consent",
    href: "/dashboard/patient-consent",
    icon: (color?: string) =>
      getMenuIcon("Patient Consent", <PersonIcon />, color),
  },
  {
    name: "Patient Consent Dashboard",
    href: "/dashboard/patient-consent-dashboard",
    icon: (color?: string) =>
      getMenuIcon("Patient Consent Dashboard", <DashboardIcon />, color),
  },
  {
    name: "External Patient Consent Dashboard",
    href: "/dashboard/external-patient-consent-dashboard",
    icon: (color?: string) =>
      getMenuIcon(
        "External Patient Consent Dashboard",
        <DashboardIcon />,
        color,
      ),
  },
  {
    name: "Feedback Survey",
    href: "/dashboard/feedback-survey",
    icon: (color?: string) =>
      getMenuIcon("Facility Assessment", <PersonIcon />, color),
  },
  {
    name: "Mock Survey",
    href: "/dashboard/mock-survey",
    icon: (color?: string) =>
      getMenuIcon("Facility Assessment", <PersonIcon />, color),
  },
  {
    name: "Company Metric",
    href: "/dashboard/company-metric",
    pageTitle: "Company Metric Configuration",
    icon: (color?: string) =>
      getMenuIcon("Company Metric", <DashboardIcon />, color),
  },
  {
    name: "Company Metric Data",
    href: "/dashboard/company-metric-data",
    pageTitle: "Company Metric Data Feed",
    icon: (color?: string) =>
      getMenuIcon("Company Metric Data", <DashboardIcon />, color),
  },
  {
    name: "Company Metric Threshold",
    href: "/dashboard/company-metric-threshold",
    pageTitle: "Metric Threshold Management",
    icon: (color?: string) =>
      getMenuIcon("Company Metric Threshold", <DashboardIcon />, color),
  },
  {
    name: "MTAP Metric",
    href: "/dashboard/mtap-metric",
    pageTitle: "MTAP Metric Configuration",
    icon: (color?: string) => getMenuIcon("MTAP Metric", <PersonIcon />, color),
  },
  {
    name: "MTAP Facility Management",
    href: "/dashboard/mtap-facility",
    pageTitle: "MTAP Facility Management",
    icon: (color?: string) =>
      getMenuIcon("MTAP Facility Management", <PersonIcon />, color),
  },
  {
    name: "My MTAP",
    href: "/dashboard/my-mtap",
    pageTitle: "My MTAP",
    icon: (color?: string) => getMenuIcon("My MTAP", <PersonIcon />, color),
  },
  {
    name: "MTAP Dashboard",
    href: "/dashboard/mtap-dashboard",
    pageTitle: "MTAP Dashboard",
    icon: (color?: string) =>
      getMenuIcon("MTAP Dashboard", <DashboardIcon />, color),
  },
  {
    name: "Employee Questionnaire",
    href: "/dashboard/employee-questionnaire",
    pageTitle: "Employee Questionnaire",
    icon: (color?: string) =>
      getMenuIcon("Employee Questionnaire", <PersonIcon />, color),
  },

  {
    name: "Provider",
    href: "/dashboard/providers",
    icon: (color?: string) => getMenuIcon("Provider", <PersonIcon />, color),
  },
  {
    name: "Profile",
    href: "/dashboard/profile",
    icon: (color?: string) => getMenuIcon("Profile", <PersonIcon />, color),
  },
  {
    name: "Settings",
    href: "/dashboard/settings",
    icon: (color?: string) => getMenuIcon("Settings", <SettingsIcon />, color),
  },
  // Support
  {
    name: "My Support Tickets",
    href: "/dashboard/support",
    icon: (color?: string) =>
      getMenuIcon("My Support Tickets", <PersonIcon />, color),
  },
  {
    name: "Support Admin",
    href: "/dashboard/support-admin",
    icon: (color?: string) =>
      getMenuIcon("Support Admin", <PersonIcon />, color),
  },
];

export default menuItems;
